import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import LoginPage from './components/LoginPage';
import AgentPage from './components/AgentPage';
import AppRouter from './components/AppRouter';
import './App.css';
import AgentConfigService from './services/AgentConfigService';
import { message } from 'antd';
import sessionStorageManager from './services/sessionStorageManager';
import SessionService from './services/SessionService';



function App() {
  // State to track if user is logged in
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  
  // State to store user information
  const [username, setUsername] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [userRole, setUserRole] = useState('');
  const [userFeatures, setUserFeatures] = useState(['HUB', 'COLAB']);
  const [defaultTab, setDefaultTab] = useState('HUB');  // NEW: Default tab state
  const [tabOrder, setTabOrder] = useState(['HUB', 'RCM', 'COLAB', 'MCP', 'COUNSEL']);

  const storedTabOrder = localStorage.getItem('tabOrder'); 

  // Check for existing session on app load
  useEffect(() => {
    const validateAndSetSession = async () => {
      const storedSessionId = localStorage.getItem('sessionId');
      const storedUsername = localStorage.getItem('username');
      const storedUserRole = localStorage.getItem('userRole');
      const storedFeatures = localStorage.getItem('userFeatures');      // NEW
      const storedDefaultTab = localStorage.getItem('defaultTab');      // NEW
      
      // Only proceed if we have the necessary data
      if (storedSessionId && storedUsername) {
        try {
          // Validate the session with the backend
          const isValid = await validateSession(storedSessionId, storedUsername, storedUserRole);
          
          if (isValid) {
            // Set state from stored values
            setSessionId(storedSessionId);
            setUsername(storedUsername);
            setUserRole(storedUserRole || 'testrole');
            setIsLoggedIn(true);
            
            // NEW: Restore features from localStorage
            if (storedFeatures) {
              try {
                setUserFeatures(JSON.parse(storedFeatures));
              } catch (e) {
                console.warn('Error parsing stored features:', e);
                setUserFeatures(['HUB', 'COLAB']);
              }
            }
            
            // NEW: Restore default tab from localStorage
            if (storedDefaultTab) {
              setDefaultTab(storedDefaultTab);
            }
            if (storedTabOrder) {
              try {
                setTabOrder(JSON.parse(storedTabOrder));
              } catch (e) {
                console.warn('Error parsing stored tab order:', e);
                setTabOrder(['HUB', 'RCM', 'COLAB', 'MCP', 'COUNSEL']);
              }
            }

            // Initialize project system in session storage manager
            try {
              const currentProjectId = sessionStorageManager.getCurrentProjectId(storedSessionId);
              if (!currentProjectId) {
                sessionStorageManager.setCurrentProjectId(storedSessionId, sessionStorageManager.DEFAULT_PROJECT_ID);
              }
            } catch (storageError) {
              console.warn('Error initializing project system:', storageError);
            }
          } else {
            // Session is invalid, clear local storage
            clearAllStorageData();
          }
        } catch (error) {
          console.error('Error validating session:', error);
          // Clear local storage on error
          clearAllStorageData();
        }
      }
    };
    
    validateAndSetSession();
  }, []); // Empty dependency array ensures this runs only once

  
  // Add this new useEffect right after the above one
  useEffect(() => {
    // Suppress ResizeObserver errors
    const handleError = (e) => {
      if (e.message === 'ResizeObserver loop completed with undelivered notifications.' ||
          e.message === 'ResizeObserver loop limit exceeded') {
        e.stopImmediatePropagation();
      }
    };
    
    window.addEventListener('error', handleError);
    
    return () => {
      window.removeEventListener('error', handleError);
    };
  }, []);




  // Function to validate session
  const validateSession = async (storedSessionId, storedUsername, storedUserRole) => {
    try {
      // Get the main API URL
      const mainApiUrl = await AgentConfigService.getMainApiUrl();
      
      // Call the session validation endpoint
      const response = await fetch(`${mainApiUrl}/session`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${storedSessionId}`
        },
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Session invalid or expired');
      }
      
      return true;
    } catch (error) {
      console.error('Session validation error:', error);
      return false;
    }
  };
  
  // Function to validate stored session
  const validateStoredSession = async (storedSessionId, storedUsername, storedUserRole) => {
    try {
      const isValid = await validateSession(storedSessionId, storedUsername, storedUserRole);
      
      if (isValid) {
        // Session is valid, set the user as logged in
        setSessionId(storedSessionId);
        setUsername(storedUsername);
        setUserRole(storedUserRole || 'testrole');
        setIsLoggedIn(true);

        // Initialize project system in session storage manager
        try {
          const currentProjectId = sessionStorageManager.getCurrentProjectId(storedSessionId);
          if (!currentProjectId) {
            sessionStorageManager.setCurrentProjectId(storedSessionId, sessionStorageManager.DEFAULT_PROJECT_ID);
          }
        } catch (storageError) {
          console.warn('Error initializing project system:', storageError);
        }
      } else {
        // Session is invalid, clear local storage
        clearAllStorageData();
      }
    } catch (error) {
      console.error('Error validating session:', error);
      // Clear local storage on error
      clearAllStorageData();
    }
  };
  
  // Helper function to clear all storage data
  const clearAllStorageData = () => {
    try {
      // Clear standard localStorage items - UPDATED: includes new keys
      const standardKeys = [
        'sessionId', 
        'username', 
        'userRole', 
        'session_id',
        'userFeatures',   // NEW
        'defaultTab',      // NEW
        'tabOrder' 
      ];
      standardKeys.forEach(key => {
        try {
          localStorage.removeItem(key);
        } catch (e) {
          console.warn(`Error removing ${key}:`, e);
        }
      });
      
      // Clear sessionStorageManager data if methods are available
      try {
        if (sessionStorageManager && typeof sessionStorageManager.clearAllSessionStorageManagerData === 'function') {
          sessionStorageManager.clearAllSessionStorageManagerData();
        } else {
          // Manual cleanup of sessionStorageManager keys
          const keys = Object.keys(localStorage);
          keys.forEach(key => {
            if (key.includes(':project:') || 
                key.startsWith('project:') ||
                key.includes('agent_state:') ||
                key.includes('agent_key:') ||
                key.includes('component_map:') ||
                key.includes('project_info:') ||
                key === 'current_project_id') {
              try {
                localStorage.removeItem(key);
              } catch (e) {
                console.warn(`Error removing sessionStorageManager key ${key}:`, e);
              }
            }
          });
        }
      } catch (e) {
        console.warn('Error clearing sessionStorageManager data:', e);
      }
      
      // Clear legacy keys
      const allKeys = Object.keys(localStorage);
      const legacyKeys = allKeys.filter(key => 
        key.startsWith('last_workflow_') || 
        key.startsWith('agent_key_') ||
        key.startsWith('component_map_') ||
        key.startsWith('agent_state_')
      );
      
      legacyKeys.forEach(key => {
        try {
          localStorage.removeItem(key);
        } catch (e) {
          console.warn(`Error removing legacy key ${key}:`, e);
        }
      });
      
      // Clear cookies
      try {
        document.cookie.split(";").forEach(cookie => {
          const eqPos = cookie.indexOf("=");
          const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
          document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
        });
      } catch (e) {
        console.warn('Error clearing cookies:', e);
      }
      
      // Clear sessionStorage
      try {
        sessionStorage.clear();
      } catch (e) {
        console.warn('Error clearing sessionStorage:', e);
      }
      
    } catch (error) {
      console.error('Error in clearAllStorageData:', error);
    }
  };
  
  // Handle successful login (old version - kept for reference)
  const handleLogin_old = (userUsername, userSessionId, userUserRole) => {
    setUsername(userUsername);
    setSessionId(userSessionId);
    setUserRole(userUserRole || 'testrole');
    setIsLoggedIn(true);
    
    // Store session information in local storage
    localStorage.setItem('sessionId', userSessionId);
    localStorage.setItem('username', userUsername);
    localStorage.setItem('userRole', userUserRole || 'testrole');
    
    // Set the session ID as a cookie as well for API requests
    document.cookie = `session_id=${userSessionId}; path=/; max-age=28800`; // 8 hours
    
    // Initialize project system in session storage manager
    try {
      if (sessionStorageManager && typeof sessionStorageManager.setCurrentProjectId === 'function') {
        sessionStorageManager.setCurrentProjectId(userSessionId, sessionStorageManager.DEFAULT_PROJECT_ID);
      }
    } catch (storageError) {
      console.warn('Error initializing project system:', storageError);
    }
    
    message.success(`Welcome, ${userUsername}!`);
  };

  

  

  /**
   * Handle successful login - UPDATED with tabOrder support
   * 
   * @param {string} userUsername - The logged in username
   * @param {string} userSessionId - The session ID from backend
   * @param {string} userUserRole - The user's role
   * @param {Array} features - Array of enabled features (e.g., ['HUB', 'COLAB', 'MCP'])
   * @param {string} userDefaultTab - The default tab to show after login (e.g., 'HUB', 'COLAB')
   * @param {Array} userTabOrder - The order of tabs to display (NEW)
   */
  const handleLogin = (userUsername, userSessionId, userUserRole, features, userDefaultTab = 'HUB', userTabOrder = null) => {
    console.log('Processing successful login...');
    console.log(`User: ${userUsername}, Role: ${userUserRole}`);
    console.log(`Features: ${JSON.stringify(features)}`);
    console.log(`Default tab: ${userDefaultTab}`);
    console.log(`Tab order: ${JSON.stringify(userTabOrder)}`);  // NEW
    
    // Use SessionService to initialize session
    SessionService.initializeSession(userUsername, userSessionId, userUserRole);
    
    // Store features, defaultTab, and tabOrder in localStorage for persistence across page refreshes
    localStorage.setItem('userFeatures', JSON.stringify(features || ['HUB', 'COLAB']));
    localStorage.setItem('defaultTab', userDefaultTab || 'HUB');
    localStorage.setItem('tabOrder', JSON.stringify(userTabOrder || ['HUB', 'RCM', 'COLAB', 'MCP', 'COUNSEL']));  // NEW
    
    // Set React state
    setUsername(userUsername);
    setSessionId(userSessionId);
    setUserRole(userUserRole || 'testrole');
    setIsLoggedIn(true);
    setUserFeatures(features || ['HUB', 'COLAB']);
    setDefaultTab(userDefaultTab || 'HUB');
    setTabOrder(userTabOrder || ['HUB', 'RCM', 'COLAB', 'MCP', 'COUNSEL']);  // NEW
    
    message.success(`Welcome, ${userUsername}!`);
  };



  /**
   * Handle logout - clears all session data and resets state
   * UPDATED: Now also resets userFeatures and defaultTab
   */
  const handleLogout = async () => {
    const success = await SessionService.logout(sessionId, null, null, {
      callParentLogout: false
    });
    
    if (success) {
      // Reset React state
      setUsername('');
      setSessionId('');
      setUserRole('');
      setUserFeatures(['HUB', 'COLAB']);
      setDefaultTab('HUB');
      setTabOrder(['HUB', 'RCM', 'COLAB', 'MCP', 'COUNSEL']);  // NEW: Reset to default
      setIsLoggedIn(false);
    }
  };
  

  const handleLogout_old = async () => {
    try {
      console.log('Starting logout process...');
      
      // Get the main API URL
      const mainApiUrl = await AgentConfigService.getMainApiUrl();
      
      // Store sessionId in a local variable before clearing state
      const currentSessionId = sessionId;
      
      // STEP 1: Call backend logout endpoint if we have a session
      if (currentSessionId) {
        console.log('Calling backend logout endpoint...');
        try {
          // Fire and forget - don't await this
          fetch(`${mainApiUrl}/logout`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${currentSessionId}`
            },
            credentials: 'include'
          }).catch(err => {
            console.log('Logout API error (non-blocking):', err);
          });
        } catch (err) {
          console.warn('Error calling logout endpoint:', err);
        }
      }
      
      // STEP 2: Clear session-specific data from sessionStorageManager
      if (currentSessionId) {
        console.log('Clearing session data from sessionStorageManager...');
        try {
          // Check if the method exists before calling it
          if (sessionStorageManager && typeof sessionStorageManager.clearSessionData === 'function') {
            const clearedSessionItems = sessionStorageManager.clearSessionData(currentSessionId);
            console.log(`Cleared ${clearedSessionItems} session-specific items`);
          } else {
            console.log('clearSessionData method not available, using manual session cleanup...');
            // Manual session cleanup
            const keys = Object.keys(localStorage);
            const sessionPrefix = `${currentSessionId}:`;
            let manualSessionCleared = 0;
            
            keys.forEach(key => {
              if (key.startsWith(sessionPrefix)) {
                try {
                  localStorage.removeItem(key);
                  manualSessionCleared++;
                } catch (e) {
                  console.warn(`Error removing session key ${key}:`, e);
                }
              }
            });
            console.log(`Manually cleared ${manualSessionCleared} session-specific items`);
          }
        } catch (err) {
          console.warn('Error clearing session data:', err);
        }
      }
      
      // STEP 3: Clear all remaining sessionStorageManager data
      try {
        console.log('Clearing all sessionStorageManager data...');
        // Check if the method exists before calling it
        if (sessionStorageManager && typeof sessionStorageManager.clearAllSessionStorageManagerData === 'function') {
          const clearedManagerItems = sessionStorageManager.clearAllSessionStorageManagerData();
          console.log(`Cleared ${clearedManagerItems} additional sessionStorageManager items`);
        } else {
          console.log('clearAllSessionStorageManagerData method not available, using manual cleanup...');
          
          // Manual cleanup of sessionStorageManager patterns
          const keys = Object.keys(localStorage);
          let manualCleared = 0;
          keys.forEach(key => {
            if (key.includes(':project:') || 
                key.startsWith('project:') ||
                key.includes('agent_state:') ||
                key.includes('agent_key:') ||
                key.includes('component_map:') ||
                key.includes('project_info:') ||
                key === 'current_project_id') {
              try {
                localStorage.removeItem(key);
                manualCleared++;
              } catch (e) {
                console.warn(`Error removing key ${key}:`, e);
              }
            }
          });
          console.log(`Manually cleared ${manualCleared} sessionStorageManager items`);
        }
      } catch (err) {
        console.warn('Error clearing sessionStorageManager data:', err);
      }
      
      // STEP 4: Clear standard localStorage items
      const standardKeys = [
        'sessionId',
        'username',
        'userRole',
        'session_id',
        'userFeatures',  // NEW
        'defaultTab'     // NEW
      ];
      
      standardKeys.forEach(key => {
        try {
          if (localStorage.getItem(key)) {
            localStorage.removeItem(key);
            console.log(`Removed standard key: ${key}`);
          }
        } catch (err) {
          console.warn(`Error removing ${key}:`, err);
        }
      });
      
      // STEP 5: Clear any legacy agent-specific data
      try {
        const allKeys = Object.keys(localStorage);
        const legacyKeys = allKeys.filter(key => 
          key.startsWith('last_workflow_') || 
          key.startsWith('agent_key_') ||
          key.startsWith('component_map_') ||
          key.startsWith('agent_state_') ||
          key.startsWith('workflow_') ||
          key.startsWith('session_')
        );
        
        legacyKeys.forEach(key => {
          try {
            localStorage.removeItem(key);
            console.log(`Removed legacy key: ${key}`);
          } catch (err) {
            console.warn(`Error removing legacy key ${key}:`, err);
          }
        });
        
        console.log(`Cleaned up ${legacyKeys.length} legacy keys`);
      } catch (err) {
        console.warn('Error during legacy cleanup:', err);
      }
      
      // STEP 6: Clear all cookies
      try {
        document.cookie.split(";").forEach(cookie => {
          const eqPos = cookie.indexOf("=");
          const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
          document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
        });
        console.log('Cleared all cookies');
      } catch (err) {
        console.warn('Error clearing cookies:', err);
      }
      
      // STEP 7: Clear sessionStorage
      try {
        sessionStorage.clear();
        console.log('Cleared sessionStorage');
      } catch (err) {
        console.warn('Error clearing sessionStorage:', err);
      }
      
      // STEP 8: Reset React state
      setUsername('');
      setSessionId('');
      setUserRole('');
      setUserFeatures(['HUB', 'COLAB']);  // NEW
      setDefaultTab('HUB');                // NEW
      setIsLoggedIn(false);
      
      console.log('Logout process completed successfully');
      message.success('Logged out successfully');
      
    } catch (error) {
      console.error('Logout error:', error);
      
      // Emergency cleanup - if all else fails, clear everything
      console.log('Performing emergency logout...');
      
      try {
        // Nuclear option - clear all localStorage
        localStorage.clear();
        sessionStorage.clear();
        
        // Clear all cookies
        document.cookie.split(";").forEach(cookie => {
          const eqPos = cookie.indexOf("=");
          const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
          document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
        });
        
        console.log('Emergency cleanup completed');
      } catch (cleanupError) {
        console.error('Emergency cleanup error:', cleanupError);
      }
      
      // Reset state regardless of errors
      setUsername('');
      setSessionId('');
      setUserRole('');
      setUserFeatures(['HUB', 'COLAB']);  // NEW
      setDefaultTab('HUB');                // NEW
      setIsLoggedIn(false);
      
      message.warning('Logout completed with warnings. You have been logged out.');
    }
  };

  return (
    <div className="App">
      <Router>
        { isLoggedIn ? (
          <AppRouter
            isLoggedIn={isLoggedIn}
            username={username}
            sessionId={sessionId}
            userRole={userRole}
            userFeatures={userFeatures}
            defaultTab={defaultTab}
            tabOrder={tabOrder}
            onLogout={handleLogout}
          />
        ) : (
          <LoginPage onLogin={handleLogin} />
        )}
      </Router>
    </div>
  );
}

export default App;